// utils/isIncognito.js
// This function checks if the browser is in incognito/private mode
// and returns a boolean value accordingly.
// Detecting incognito mode is inherently unreliable and may not work in all browsers or future versions.
// This function attempts various methods to make and educated guess.
// Better to use storageIsEphemeral() where possible.
export async function isIncognito() {
  // Chrome / Edge / Opera detection
  const fs = window.RequestFileSystem || window.webkitRequestFileSystem;
  if (fs) {
    return new Promise((resolve) => {
      fs(window.TEMPORARY, 100, () => resolve(false), () => resolve(true));
    });
  }

  // Firefox detection — refined quota + persist test
  if ('storage' in navigator && 'estimate' in navigator.storage) {
    try {
      const { quota } = await navigator.storage.estimate();

      // Typical private mode quota is ~0–100 MB vs 2GB+ in normal
      if (quota && quota < 150 * 1024 * 1024) return true;

      // In normal mode, persisted storage is allowed
      if (navigator.storage.persisted) {
        const persisted = await navigator.storage.persisted();
        if (!persisted) return true;
      }

      return false;
    } catch {
      return true;
    }
  }

  // Safari / iOS detection via IndexedDB
  try {
    return await new Promise((resolve) => {
      const db = indexedDB.open('test');
      db.onerror = () => resolve(true);
      db.onsuccess = () => {
        db.result.close();
        indexedDB.deleteDatabase('test');
        resolve(false);
      };
    });
  } catch {
    return true;
  }
}

// Detect that storage is ephemeral and may be lost
// Returns true if storage is likely ephemeral (e.g., incognito/private mode)
// Note: This function is more reliable than isIncognito
export async function isStorageEphemeral() {
  if (!navigator.storage) return true;

  try{
    if(!navigator.storage.persisted) return true;

    const persisted = await navigator.storage.persisted();
    if (!persisted) return false;

    // Try to request persistence (no prompt in Safari)
    if(navigator.storage.persist) {
      const granted = await navigator.storage.persist();
      return !granted;
    }
  }finally{
    return true;
  }
}
